import { useContext, useMemo } from 'react';
import { PolicyActionEnum } from '@trinity/access-policy';
import { useCan } from '@trinity/access-policy/react';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';

export type SegmentCapabilities = {
  canCreate: boolean;
  canEdit: boolean;
  /** Deleting needs the delete action and manage access to the project (as the route does). */
  canDelete: boolean;
  canPublish: boolean;
  /** Placing a widget on a segment dashboard saves the dashboard page, so it needs the edit action. */
  canAddToDashboard: boolean;
};

/** UI gating only: the project segment routes in chat-api enforce the same actions. */
export const useSegmentCapabilities = (): SegmentCapabilities => {
  const canCreate = useCan(PolicyActionEnum.IeSegmentCreate);
  const canEdit = useCan(PolicyActionEnum.IeSegmentEdit);
  const canDeleteSegment = useCan(PolicyActionEnum.IeSegmentDelete);
  const canPublish = useCan(PolicyActionEnum.IeSegmentPublish);
  // Absent outside a project page (e.g. the standalone segment dashboard); false while loading.
  const projectDetails = useContext(ProjectDetailsContext);
  const canManageProject = projectDetails?.query.data?.capabilities.canManage === true;

  return useMemo(
    () => ({
      canCreate,
      canEdit,
      canDelete: canDeleteSegment && canManageProject,
      canPublish,
      canAddToDashboard: canEdit,
    }),
    [canCreate, canEdit, canDeleteSegment, canManageProject, canPublish]
  );
};
